import React, { useEffect } from 'react'
import { withStyles } from '@material-ui/core/styles'
import { FormattedMessage, useIntl } from 'react-intl'
import Assets from 'app/configs/assetConfigs'
import * as R from 'ramda'
import RichTextDisplay from 'app/components/richTextEditor/RichTextDisplay'
import {
  Typography,
  makeStyles,
  Button,
  FormControlLabel,
  Avatar,
} from '@material-ui/core'
import effects from 'app/effects/effects'
import moment from 'moment'
import PrimaryButton from 'app/components/buttons/PrimaryButton'
import DeleteButton from 'app/components/buttons/DeleteButton'
import RedemptionActions from './RedemptionActions'
import RedemptionUnsuccessful from './RedemptionUnsuccessful'
import Dialog from '@material-ui/core/Dialog'
import DialogTitle from '@material-ui/core/DialogTitle'
import DialogActions from '@material-ui/core/DialogActions'
import ErrorView from 'app/components/ErrorView'
import useRedemption, {
  SET_VOUCHER_INFO,
  SET_IS_ERROR,
  SET_APPROVE_SUCCESS,
  SET_REJECTED_SUCCESS,
  SET_REJECTED_ERROR,
  OPEN_CONFIRM_DIALOG,
  SET_IS_NOTIFICATION_NOT_FOUND,
  CLOSE_CONFIRM_DIALOG,
} from './useRedemption'
import clsx from 'clsx'
import { DATE_FORMAT_VOUCHER } from 'libs/dateUtils'
import queryString from 'query-string'
import { useAppLogo } from 'commons/usePartner'

const styles = (theme) => ({
  extendedIcon: {
    marginRight: theme.spacing(1),
  },
})

const NOTIFICATION_NOT_FOUND_CODE = 629

const useStyles = makeStyles((theme) => ({
  titleRedemption: {
    padding: '2vw',
    fontSize: '6vw',
    fontWeight: 'bold',
    color: '#064e75',
  },
  large: {
    width: '110px',
    height: '110px',
    margin: 'auto',
  },
  subscriberName: {
    fontSize: '5vw',
    fontWeight: 'bold',
    color: '#064e75',
    paddingBottom: '3px',
    paddingTop: '10px',
  },
  subTitleRedemption: {
    fontSize: '4vw',
    color: '#064e75',
  },

  btnApprove: {
    width: '80%',
    fontSize: '3.5vw',
    lineHeight: '2.0',
    margin: '4vw',
    borderRadius: '1rem',
  },
  btnReject: {
    width: '80%',
    fontSize: '3.5vw',
    lineHeight: '2.0',
    margin: '2vw',
    marginTop: '0px',
    borderRadius: '1rem',
  },
  titleIcon: {
    width: '9vw',
  },
  titleSection: {
    padding: '4vw',
    height: '20%',
    minHeight: '100px',
    '& label': {
      padding: '10px',
      margin: 'auto',
    },
  },
  bodySection: {
    height: '40%',
    minHeight: '180px',
  },
  footerSection: {
    fontSize: '8vw',
    fontWeight: 'bold',
    color: '#00adee',
    textAlign: 'center',
    height: '20%',
    minHeight: '100px',
    paddingTop: '20px',
  },
  rootRedemptionPage: {
    overflowY: 'scroll',
  },
  footerRedemption: {
    backgroundColor: '#103b5f',
    textAlign: 'center',
    padding: '20px',
    minHeight: '100px',
    bottom: 0,
    width: '100%',
  },
}))

const Redemption = (props) => {
  const classes = useStyles()
  const { history, location } = props
  const { alertId, voucherId, parentId } = queryString.parse(location.search)
  const { formatMessage } = useIntl()
  const { logo } = useAppLogo()

  const [
    {
      user,
      voucher,
      openRejectConfirmDialog,
      isApproved,
      isRejected,
      isError,
      isNotificationNotFound,
    },
    dispatchRedemption,
  ] = useRedemption()

  const isRedeemed = R.prop('isRedeemed')(voucher)

  const isUsedOverLimit = R.compose(
    R.equals(1),
    R.prop('isUsedOverLimit'),
  )(voucher)

  const isRejectedFromResponse = R.compose(
    R.equals(1),
    R.prop('isRejected'),
  )(voucher)

  const isCanceled = R.compose(R.not, R.isNil, R.prop('canceledDate'))(voucher)

  const isExpired = R.ifElse(
    R.isEmpty,
    R.always(false),
    R.always(moment(R.prop('endTime', voucher)).isBefore(moment())),
  )(voucher)

  const getUnsuccessMessage = () => {
    if (isRejectedFromResponse)
      return formatMessage({ id: 'redemption.voucher.unsuccess.rejected' })

    if (isCanceled)
      return formatMessage(
        {
          id: 'redemption.voucher.unsuccess.canceled',
        },
        {
          date: moment(R.prop('canceledDate', voucher)).format(
            DATE_FORMAT_VOUCHER,
          ),
        },
      )

    if (isUsedOverLimit)
      return formatMessage({
        id: 'redemption.voucher.unsuccess.redemptionLimit',
      })

    if (isExpired)
      return formatMessage(
        {
          id: 'redemption.voucher.unsuccess.expired',
        },
        {
          date: moment(R.prop('endTime', voucher)).format(DATE_FORMAT_VOUCHER),
        },
      )

    if (isNotificationNotFound)
      return formatMessage({
        id: 'redemption.voucher.unsuccess.does.not.belong.place',
      })

    if (isRedeemed)
      return formatMessage({
        id: 'redemption.voucher.unsuccess.has.been.redeemed',
      })

    return null
  }

  useEffect(() => {
    effects.apis
      .getVoucherInfo({
        voucherId,
        parentId,
      })
      .then((response) => {
        dispatchRedemption({
          type: SET_VOUCHER_INFO,
          user: R.prop('user', response),
          voucher: R.prop('voucher', response),
        })
      })
      .catch((error) => {
        if (
          R.path(['response', 'data', 'details', 'code'], error) ===
          NOTIFICATION_NOT_FOUND_CODE
        ) {
          dispatchRedemption({
            type: SET_IS_NOTIFICATION_NOT_FOUND,
          })
        } else {
          dispatchRedemption({ type: SET_IS_ERROR })
        }
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voucherId, parentId])

  const voucherTitle = R.ifElse(
    R.isEmpty,
    R.always(''),
    R.prop('title'),
  )(voucher)

  const handleApprove = () => {
    effects.apis.authV2.voucher
      .approve({
        id: Number(voucherId),
        parentId: Number(parentId),
      })
      .then((response) => {
        dispatchRedemption({ type: SET_APPROVE_SUCCESS })
      })
      .catch((error) => {
        dispatchRedemption({ type: SET_IS_ERROR })
      })
  }

  const handleReject = () => {
    effects.apis.authV2.voucher
      .reject({
        id: Number(voucherId),
        parentId: Number(parentId),
      })
      .then((response) => {
        dispatchRedemption({ type: SET_REJECTED_SUCCESS })
      })
      .catch((error) => {
        dispatchRedemption({ type: SET_REJECTED_ERROR })
      })
  }

  const openDialog = () => {
    dispatchRedemption({ type: OPEN_CONFIRM_DIALOG })
  }

  if (isError) {
    return <ErrorView />
  }

  if (isApproved || isRejected) {
    return (
      <RedemptionActions
        user={user}
        voucher={voucher}
        voucherTitle={voucherTitle}
        isApproved={isApproved}
        isRejected={isRejected}
        history={history}
      />
    )
  }

  if (
    isRedeemed ||
    isCanceled ||
    isRejectedFromResponse ||
    isExpired ||
    isUsedOverLimit ||
    isNotificationNotFound
  ) {
    return (
      <RedemptionUnsuccessful
        user={user}
        voucher={voucher}
        voucherTitle={voucherTitle}
        history={history}
        isNotificationNotFound={isNotificationNotFound}
        message={getUnsuccessMessage()}
      />
    )
  }

  if (R.isEmpty(user)) {
    return null
  }

  return (
    <>
      <div className={clsx('h-full', classes.rootRedemptionPage)}>
        <div className={clsx(classes.titleSection, 'w-full text-center')}>
          <FormControlLabel
            control={
              <img
                className={clsx(classes.titleIcon, 'sm:w-48')}
                src={logo}
                alt="titleIcon"
              />
            }
            label={
              <div className={clsx(classes.titleRedemption, 'sm:text-4xl')}>
                <FormattedMessage id="redemption.voucher.redemption" />
              </div>
            }
            disabled={true}
          />
        </div>
        <div className={clsx(classes.bodySection, 'w-full text-center')}>
          <Avatar
            alt="Subscribers Image"
            src={R.defaultTo(Assets.defaultPlaceImage, R.prop('avatar', user))}
            className={classes.large}
          />
          <Typography className={clsx(classes.subscriberName, 'sm:text-2xl')}>
            {`${R.prop('firstName', user)} ${R.prop('lastName', user)}`}
          </Typography>
          <Typography
            className={clsx(classes.subTitleRedemption, 'sm:text-lg')}
          >
            <FormattedMessage id="redemption.voucher.redemption.is.followingItem" />
          </Typography>
        </div>
        <div
          className={clsx(
            classes.footerSection,
            'w-full text-center sm:text-5xl',
          )}
        >
          <RichTextDisplay text={voucherTitle} />
        </div>
        <div className={clsx(classes.footerRedemption, 'absolute sm:static')}>
          <div>
            <PrimaryButton
              className={clsx(classes.btnApprove, 'sm:w-4/6 sm:text-lg')}
              onClick={handleApprove}
              inputProps={{
                'data-testid': 'approveRedemption',
              }}
            >
              <FormattedMessage id="subscriber.approve.button.label" />
            </PrimaryButton>
          </div>
          <div>
            <DeleteButton
              onClick={openDialog}
              className={clsx(classes.btnReject, 'sm:w-4/6 sm:text-lg')}
              msgId="action.reject"
              inputProps={{
                'data-testid': 'openConfirmDialogRejectRedemptionBtn',
              }}
            />
          </div>
        </div>
      </div>

      <Dialog
        aria-labelledby="simple-dialog-title"
        open={openRejectConfirmDialog}
      >
        <DialogTitle id="simple-dialog-title">
          <FormattedMessage
            id="redemption.voucher.redemption.reject.dialog"
            values={{
              b: (text) => (
                <>
                  <br />
                  {text}
                </>
              ),
              firstName: R.prop('firstName', user),
              lastName: R.prop('lastName', user),
            }}
          />
        </DialogTitle>
        <DialogActions>
          <Button
            onClick={() => dispatchRedemption({ type: CLOSE_CONFIRM_DIALOG })}
            color="primary"
            inputProps={{
              'data-testid': 'closeConfirmDialogRejectRedemptionBtn',
            }}
          >
            <FormattedMessage id="action.no" />
          </Button>
          <Button
            onClick={handleReject}
            color="primary"
            inputProps={{
              'data-testid': 'rejectRedemptionBtn',
            }}
          >
            <FormattedMessage id="action.yes" />
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default withStyles(styles, { withTheme: true })(Redemption)
